import React, { useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/joy';
import AudioGuide from './AudioGuide';
import { computeHouseCombinedScore } from '../utils/houseTemporalScores';
import { useIsMobile } from '../hooks/useIsMobile';
import { computeAllHouseLFIs, getFrictionLevel, FRICTION_LEVEL_COLORS } from '../astro/karmic/karmicFrictionEngine';

interface RemedyItem {
  title: string;
  description?: string;
  planet: string;
  isImportant?: boolean;
}

interface LifeAreasDashboardProps {
  houseScores: Record<number, number> | null;
  bhavaBala?: Record<number, number> | null;
  dashaImpactByHouse?: Record<number, number>;
  transitImpactByHouse?: Record<number, number>;
  remedies?: Record<string, RemedyItem[]>;
  onNavigateToRemedies?: () => void;
  onNavigateToLifeAreaRemedies?: (lifeAreaName: string) => void;
  onGetOverview?: (lifeAreaName: string) => void;
  aiCreditsLifeArea?: number | null;
  /** Planet friction scores (0-100 per planet) for Life Friction Index bar */
  frictionScoresByPlanet?: Record<string, number>;
  /** Planet placements map (planet -> house) needed for LFI computation */
  planetPlacements?: Record<string, { house: number }>;
  /** Ascendant sign index (1-12) needed for LFI computation */
  ascendantSignIndex?: number;
}

// Per-rating color theme (pastel borders/accents)
const COLOR_THEMES = {
  success: { bg: '#f0fdf4', border: '#86efac', accent: '#22c55e', barBg: '#bbf7d0', light: '#dcfce7', text: '#15803d', dosBg: '#f0fdf4' },
  primary: { bg: '#eff6ff', border: '#93c5fd', accent: '#3b82f6', barBg: '#bfdbfe', light: '#dbeafe', text: '#1d4ed8', dosBg: '#eff6ff' },
  warning: { bg: '#fffbeb', border: '#fcd34d', accent: '#f59e0b', barBg: '#fde68a', light: '#fef3c7', text: '#b45309', dosBg: '#fffbeb' },
  danger:  { bg: '#fef2f2', border: '#fca5a5', accent: '#f87171', barBg: '#fecaca', light: '#fee2e2', text: '#b91c1c', dosBg: '#fef2f2' },
} as const;

const LIFE_AREAS = [
  { name: 'Health',   houses: [1, 6, 8, 12], icon: '❤️', description: 'Physical vitality, diseases, chronic issues, hospitalization' },
  { name: 'Wealth',   houses: [1, 2, 5, 9, 11, 12], icon: '💰', description: 'Income, assets, speculations, fortune, gains, expenses' },
  { name: 'Job',      houses: [1, 6, 10], icon: '💼', description: 'Career, service, professional reputation' },
  { name: 'Business', houses: [1, 3, 7, 11], icon: '📈', description: 'Entrepreneurship, partnerships, gains from ventures' },
  { name: 'Marriage', houses: [1, 7], icon: '💑', description: 'Spouse, partnerships, marital harmony' },
  { name: 'Children', houses: [1, 5], icon: '👶', description: 'Children, creativity, romance' },
];

function getRatingKey(score: number): keyof typeof COLOR_THEMES {
  if (score >= 75) return 'success';
  if (score >= 60) return 'primary';
  if (score >= 30) return 'warning';
  return 'danger';
}

function getRatingLabel(score: number): string {
  if (score >= 75) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 30) return 'Fair';
  return 'Needs Attention';
}

const toSignedDisplay = (v: number) => {
  const d = Math.round(v - 50);  // −50..+50, 0 = neutral
  return (d >= 0 ? '+' : '') + d;
};

function ScoreLegend() {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mb: 2.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.75, py: 1.25, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'background.level2' } }}
      >
        <Typography level="title-sm" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: 'primary.main' }}>📊 How to Read Your Scores</Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.75rem' }}>{open ? '▲ Hide' : '▼ Show'}</Typography>
      </Box>
      {open && (
        <Box sx={{ px: 1.75, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
            Each life area score is a <strong>weighted blend</strong> of four metrics — natal factors (Bhava Bala + Planet Impact) at <strong>65%</strong> and live temporal factors (Dasha + Transit) at <strong>35%</strong>.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {[
              { color: '#2563eb', title: 'Bhava Bala — Structural Resilience', desc: 'Classical astronomical house strength. High Bhava Bala = strong structural support even during challenging periods.' },
              { color: '#d97706', title: 'Planet Impact — Karmic Delivery Score', desc: 'Blended Shadbala-based score factoring house lord strength, occupants, and aspectual impact.' },
              { color: '#a78bfa', title: 'Dasha Impact — Current Period Activation', desc: 'Displayed as −50 to +50 (0 = neutral). Active MD/AD/PD/SD lords ruling, occupying, or aspecting these houses shifts the bar positive or negative.' },
              { color: '#22d3ee', title: 'Transit Impact — Live Planetary Influence', desc: 'Displayed as −50 to +50 (0 = neutral). Benefic transits push positive; malefic transits push negative.' },
            ].map(({ color, title, desc }) => (
              <Box key={title} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Box sx={{ mt: '4px', width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Box>
                  <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>{title}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.55, fontSize: '0.75rem' }}>{desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function LifeAreasDashboard({
  houseScores,
  bhavaBala,
  dashaImpactByHouse,
  transitImpactByHouse,
  remedies,
  onNavigateToRemedies,
  onNavigateToLifeAreaRemedies,
  onGetOverview,
  aiCreditsLifeArea = null,
  frictionScoresByPlanet,
  planetPlacements,
  ascendantSignIndex,
}: LifeAreasDashboardProps) {

  const { isMobile } = useIsMobile();

  const computedAreas = useMemo(() => {
    if (!houseScores) return null;
    const hasAll = Array.from({ length: 12 }, (_, i) => i + 1)
      .every(h => typeof houseScores[h] === 'number' && Number.isFinite(houseScores[h]));
    if (!hasAll) return null;

    return LIFE_AREAS.map(area => {
      const avg = (map: Record<number, number> | null | undefined, fallback: number) => {
        const vals = area.houses.map(h => map?.[h] ?? fallback);
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      };

      const bhavaScore    = avg(bhavaBala ?? null, 50);
      const planetImpact  = avg(houseScores, 0);
      const dashaImpact   = avg(dashaImpactByHouse ?? null, 50);
      const transitImpact = avg(transitImpactByHouse ?? null, 50);
      const combined      = computeHouseCombinedScore(bhavaScore, planetImpact, dashaImpact, transitImpact);

      const normalizedPlanetPct = ((planetImpact + 50) / 100) * 100;
      const areaRemedies = remedies?.[area.name] ?? [];

      // Life Friction Index: average LFI of the area's houses
      let areaLFI: number | null = null;
      if (frictionScoresByPlanet && planetPlacements && ascendantSignIndex &&
          Object.keys(frictionScoresByPlanet).length > 0) {
        const allHouseLFIs = computeAllHouseLFIs(planetPlacements, ascendantSignIndex, frictionScoresByPlanet);
        const lfiValues = area.houses.map(h => allHouseLFIs[h]?.lfi ?? null).filter((v): v is number => v !== null);
        areaLFI = lfiValues.length > 0 ? Math.round(lfiValues.reduce((a, b) => a + b, 0) / lfiValues.length) : null;
      }

      return {
        ...area,
        bhavaScore,
        planetImpact,
        dashaImpact,
        transitImpact,
        normalizedPlanetPct,
        combined,
        areaRemedies,
        areaLFI,
      };
    });
  }, [houseScores, bhavaBala, dashaImpactByHouse, transitImpactByHouse, remedies, frictionScoresByPlanet, planetPlacements, ascendantSignIndex]);

  if (!computedAreas) {
    return (
      <Box sx={{ p: 2, borderRadius: 'md', border: '1px dashed', borderColor: 'neutral.outlinedBorder', bgcolor: 'neutral.softBg', textAlign: 'center' }}>
        <Typography level="body-sm" sx={{ opacity: 0.75 }}>Calculate your chart to see Life Areas Dashboard</Typography>
      </Box>
    );
  }

  const sortedAreas = [...computedAreas].sort((a, b) => b.combined - a.combined);
  const strongAreas = sortedAreas.filter(a => a.combined >= 65);
  const growthAreas = sortedAreas.filter(a => a.combined < 55);

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="h4" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 600 }}>🌟 Life Areas Dashboard</Typography>
          <AudioGuide src="/audio/guide-life-areas.mp3" label="Life Areas Guide" variant="red" />
        </Box>
        {onNavigateToRemedies && (
          <Button size="sm" variant="outlined" color="primary" onClick={onNavigateToRemedies}>
            🌿 View Remedies
          </Button>
        )}
      </Box>

      {/* Score legend — collapsible, hidden on mobile by default */}
      {!isMobile && (
      <ScoreLegend />
      )}

      {/* Area cards grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        {computedAreas.map((area) => {
          const ratingKey = getRatingKey(area.combined);
          const theme = COLOR_THEMES[ratingKey];
          const importantRemedies = area.areaRemedies.filter(r => r.isImportant);
          const normalRemedies = area.areaRemedies.filter(r => !r.isImportant);

          const bars = [
            { label: 'Bhava Bala', value: area.bhavaScore, color: theme.accent, barBg: theme.barBg, bipolar: false,
              displayLabel: `${area.bhavaScore.toFixed(1)}%` },
            { label: 'Planet Impact', value: area.normalizedPlanetPct, color: '#fbbf24', barBg: '#fef3c7', bipolar: true,
              displayLabel: `${area.planetImpact >= 0 ? '+' : ''}${area.planetImpact.toFixed(1)}` },
            { label: 'Dasha Impact', value: area.dashaImpact, color: '#a78bfa', barBg: '#ede9fe', bipolar: true,
              displayLabel: toSignedDisplay(area.dashaImpact), live: true },
            { label: 'Transit Impact', value: area.transitImpact, color: '#22d3ee', barBg: '#cffafe', bipolar: true,
              displayLabel: toSignedDisplay(area.transitImpact), live: true },
            ...(area.areaLFI !== null ? [{
              label: 'Life Friction',
              value: area.areaLFI,
              color: FRICTION_LEVEL_COLORS[getFrictionLevel(area.areaLFI)],
              barBg: area.areaLFI <= 33 ? '#dcfce7' : area.areaLFI <= 60 ? '#fef3c7' : '#fee2e2',
              bipolar: false,
              displayLabel: `${area.areaLFI}%`,
              frictionLevel: getFrictionLevel(area.areaLFI),
            }] : []),
          ];

          return (
            <Box key={area.name} sx={{
              position: 'relative', overflow: 'hidden', borderRadius: '12px',
              border: `1.5px solid ${theme.light}`,
              background: `linear-gradient(145deg, ${theme.bg} 0%, #ffffff 60%)`,
              boxShadow: `0 2px 12px ${theme.border}18`,
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Icon watermark */}
              <Box aria-hidden sx={{
                position: 'absolute', bottom: '-8px', right: '-4px', fontSize: '6rem',
                lineHeight: 1, opacity: 0.07, pointerEvents: 'none', userSelect: 'none',
              }}>{area.icon}</Box>

              {/* Top accent strip */}
              <Box sx={{ height: '4px', background: `linear-gradient(90deg, ${theme.border}, ${theme.accent}88)`, borderRadius: '12px 12px 0 0' }} />

              {/* Card body */}
              <Box sx={{ p: isMobile ? 1.25 : 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>

                {/* Header: icon + name + score pill */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{area.icon}</span>
                    <Typography level="title-sm" sx={{ fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: '0.9rem', color: theme.text }}>{area.name}</Typography>
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    background: theme.light, borderRadius: '20px',
                    px: 1, py: 0.3, border: `1px solid ${theme.border}40`,
                  }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: theme.text, lineHeight: 1 }}>{area.combined}%</Typography>
                    <Box sx={{ width: '1px', height: '10px', bgcolor: `${theme.border}40` }} />
                    <Typography sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.65rem', color: theme.text, lineHeight: 1 }}>{getRatingLabel(area.combined)}</Typography>
                  </Box>
                </Box>

                {/* metric bars */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {bars.map(({ label, value, color, barBg, bipolar, displayLabel, live, frictionLevel }: any) => {
                    const pct = Math.max(0, Math.min(100, value));
                    return (
                      <Box key={label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.58rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                            {live && (
                              <Box sx={{ background: '#f3f4f6', borderRadius: '4px', px: 0.4, lineHeight: 1 }}>
                                <Typography sx={{ fontSize: isMobile ? '0.65rem' : '0.48rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>now</Typography>
                              </Box>
                            )}
                            {frictionLevel && (
                              <Box sx={{ background: color + '22', borderRadius: '4px', px: 0.4, lineHeight: 1 }}>
                                <Typography sx={{ fontSize: isMobile ? '0.65rem' : '0.48rem', fontWeight: 700, textTransform: 'uppercase', color }}>{frictionLevel}</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.58rem', fontWeight: 700, color }}>{displayLabel}</Typography>
                        </Box>
                        {!bipolar ? (
                          <Box sx={{ height: '5px', borderRadius: '3px', background: barBg, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.3s' }} />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ height: '5px', borderRadius: '3px', background: '#f3f4f6', position: 'relative', overflow: 'hidden' }}>
                              <Box sx={{ position: 'absolute', left: '50%', top: 0, width: '1px', height: '100%', background: '#d1d5db', zIndex: 1 }} />
                              {Math.abs(pct - 50) > 0 && (
                                <Box sx={{
                                  position: 'absolute', top: 0, height: '100%', borderRadius: '3px',
                                  ...(pct > 50
                                    ? { left: '50%', width: `${pct - 50}%`, background: color }
                                    : { right: '50%', width: `${50 - pct}%`, background: '#dc2626' }
                                  ),
                                }} />
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Description */}
                <Typography sx={{ fontSize: isMobile ? '0.78rem' : '0.62rem', color: '#6b7280', lineHeight: 1.45 }}>{area.description}</Typography>

                {/* Do's & Don'ts remedies */}
                {area.areaRemedies.length > 0 && (
                  <Box sx={{ mt: 'auto', borderRadius: '8px', border: `1px solid ${theme.border}30`, overflow: 'hidden' }}>
                    <Box sx={{ px: 1, py: 0.6, background: `linear-gradient(90deg, ${theme.border}18, ${theme.accent}08)`, borderBottom: `1px solid ${theme.border}25`, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.text }}>🌿 Remedies</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography sx={{ fontSize: '0.55rem', color: theme.text, opacity: 0.7, fontStyle: 'italic' }}>
                        {area.areaRemedies.length} remedy{area.areaRemedies.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {importantRemedies.map((remedy: RemedyItem, idx: number) => (
                      <Box key={`imp-${idx}`} sx={{ px: 1, py: 0.6, background: 'linear-gradient(90deg, #fff7ed, #fffbeb)', borderBottom: idx < importantRemedies.length - 1 || normalRemedies.length > 0 ? '1px solid #fed7aa40' : 'none', display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                        <Box sx={{ width: '14px', height: '14px', borderRadius: '50%', background: '#f97316', flexShrink: 0, mt: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ fontSize: '0.5rem', color: '#fff', lineHeight: 1, fontWeight: 800 }}>!</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mb: '2px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '0.75rem' : '0.65rem', lineHeight: 1.3, color: '#c2410c' }}>{remedy.title}</Typography>
                            <Box sx={{ background: '#fed7aa', borderRadius: '4px', px: 0.5, py: 0.1, lineHeight: 1, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.55rem', color: '#9a3412', fontStyle: 'italic', opacity: 0.75 }}>{remedy.planet}</Typography>
                          </Box>
                          {remedy.description && <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.6rem', color: '#92400e', lineHeight: 1.35 }}>{remedy.description}</Typography>}
                        </Box>
                      </Box>
                    ))}
                    {normalRemedies.map((remedy: RemedyItem, idx: number) => (
                      <Box key={`norm-${idx}`} sx={{ px: 1, py: 0.6, background: idx % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: idx < normalRemedies.length - 1 ? `1px solid ${theme.border}15` : 'none', display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: '0.65rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>🌿</span>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mb: '2px' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.65rem', lineHeight: 1.3, color: '#1f2937' }}>{remedy.title}</Typography>
                            <Typography sx={{ fontSize: '0.55rem', color: '#6b7280', fontStyle: 'italic' }}>{remedy.planet}</Typography>
                          </Box>
                          {remedy.description && <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.6rem', color: '#6b7280', lineHeight: 1.35 }}>{remedy.description}</Typography>}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Action row */}
                {(onGetOverview || onNavigateToLifeAreaRemedies) && (
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                    {onGetOverview && (
                      <Button size="sm" variant="outlined" sx={{ fontSize: '0.65rem', py: 0.3, px: 1, minHeight: 0, borderColor: theme.border, color: theme.text, '&:hover': { bgcolor: theme.light } }}
                        onClick={() => onGetOverview(area.name)}>
                        🤖 Get Life Area Prediction
                        {aiCreditsLifeArea !== null && (
                          <Box component="span" sx={{ ml: 0.5, px: 0.6, py: '1px', borderRadius: '8px', fontSize: '0.55rem', fontWeight: 700, lineHeight: '13px', bgcolor: aiCreditsLifeArea === 0 ? '#fee2e2' : aiCreditsLifeArea <= 2 ? '#fef3c7' : '#dcfce7', color: aiCreditsLifeArea === 0 ? '#dc2626' : aiCreditsLifeArea <= 2 ? '#b45309' : '#16a34a' }}>
                            {aiCreditsLifeArea} left
                          </Box>
                        )}
                      </Button>
                    )}
                    {onNavigateToLifeAreaRemedies && area.areaRemedies.length === 0 && (
                      <Button size="sm" variant="soft" sx={{ fontSize: '0.65rem', py: 0.3, px: 1, minHeight: 0 }}
                        onClick={() => onNavigateToLifeAreaRemedies(area.name)}>
                        🌿 Remedies
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Strongest / Growth summary */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mt: 2, zoom: isMobile ? 1.15 : 1 }}>
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #bbf7d0', background: 'linear-gradient(135deg, #f0fdf4, #ffffff)', boxShadow: '0 2px 8px #16a34a14' }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #16a34a1a, #16a34a06)', borderBottom: '1px solid #bbf7d0' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#15803d' }}>✨ Strongest Areas</Typography>
          </Box>
          <Box sx={{ p: 1.25 }}>
            {strongAreas.slice(0, 3).map((area, i) => (
              <Box key={area.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: i < 2 ? 0.5 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', width: '14px' }}>{i + 1}.</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1f2937' }}>{area.icon} {area.name}</Typography>
                </Box>
                <Box sx={{ background: '#dcfce7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d' }}>{area.combined}%</Typography>
                </Box>
              </Box>
            ))}
            {strongAreas.length === 0 && <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontStyle: 'italic' }}>Chart analysis pending</Typography>}
          </Box>
        </Box>

        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #fde68a', background: 'linear-gradient(135deg, #fffbeb, #ffffff)', boxShadow: '0 2px 8px #d9770614' }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #d977061a, #d9770606)', borderBottom: '1px solid #fde68a' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#b45309' }}>🎯 Areas for Growth</Typography>
          </Box>
          <Box sx={{ p: 1.25 }}>
            {growthAreas.slice(0, 3).map((area, i) => (
              <Box key={area.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: i < 2 ? 0.5 : 0 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1f2937' }}>{area.icon} {area.name}</Typography>
                <Box sx={{ background: '#fef3c7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309' }}>{area.combined}%</Typography>
                </Box>
              </Box>
            ))}
            {growthAreas.length === 0 && <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontStyle: 'italic' }}>All areas show strength</Typography>}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
